import React, { useMemo, useState } from 'react';
import { APP_THEMES, DEFAULT_APP_THEME_ID, DEFAULT_SIGNAL_VOLUME, getThemeMeta } from './SettingsContext';
import { useLang } from './LangContext';
import { getT } from './translations';

export const SETTINGS_DEFAULTS = {
  pomodoro: 25,
  shortBreak: 5,
  longBreak: 30,
  intervals: 4,
  countdown: true,
  autoStart: false,
  skipControls: false,
  extensionControls: false,

  showShortcutHints: true,
  themeId: DEFAULT_APP_THEME_ID,
  signalVolume: DEFAULT_SIGNAL_VOLUME,
  presetId: 'classic',
};

const TIMER_PRESETS = [
  {
    id: 'classic',
    name: 'Classic',
    description: '25 / 5 · 4 rounds',
    pomodoro: 25,
    shortBreak: 5,
    longBreak: 30,
    intervals: 4,
  },
  {
    id: 'deep-work',
    name: 'Deep Work',
    description: '50 / 10 · 3 rounds',
    pomodoro: 50,
    shortBreak: 10,
    longBreak: 30,
    intervals: 3,
  },
  {
    id: 'sprint',
    name: 'Sprint',
    description: '15 / 3 · 4 rounds',
    pomodoro: 15,
    shortBreak: 3,
    longBreak: 15,
    intervals: 4,
  },
  {
    id: 'study',
    name: 'Study',
    description: '45 / 15 · 3 rounds',
    pomodoro: 45,
    shortBreak: 15,
    longBreak: 30,
    intervals: 3,
  },
];

const SETTINGS_NUMBER_MIN = 1;
const SETTINGS_NUMBER_MAX = 999;

const TIMER_FIELD_CONFIG = [
  { key: 'pomodoro', id: 'pomodoro-time', labelKey: 'settingsLabelPomodoro' },
  { key: 'shortBreak', id: 'short-break-time', labelKey: 'settingsLabelShortBreak' },
  { key: 'longBreak', id: 'long-break-time', labelKey: 'settingsLabelLongBreak' },
  { key: 'intervals', id: 'intervals', labelKey: 'settingsLabelIntervals' },
];

const BEHAVIOR_TOGGLE_CONFIG = [
  { key: 'countdown', id: 'enable-countdown', labelKey: 'settingsLabelCountdown' },
  { key: 'autoStart', id: 'auto-start-pomodoros', labelKey: 'settingsLabelAutoStart' },
  { key: 'skipControls', id: 'enable-skip-controls', labelKey: 'settingsLabelSkipControls' },
  { key: 'extensionControls', id: 'enable-quick-extension-controls', labelKey: 'settingsLabelExtensionControls' },
];

const APPEARANCE_TOGGLE_CONFIG = [
  { key: 'showShortcutHints', id: 'show-shortcut-hints', labelKey: 'settingsLabelShowShortcutHints' },
];

const SETTINGS_CATEGORY_ORDER = ['timer', 'behavior', 'appearance', 'alerts'];

export function normalizeIntegerSetting(value) {
  return Math.max(SETTINGS_NUMBER_MIN, Math.min(SETTINGS_NUMBER_MAX, parseInt(value, 10) || SETTINGS_NUMBER_MIN));
}

export function normalizeSignalVolume(value) {
  return Math.min(1, Math.max(0, Number.parseFloat(value) || 0));
}

function NumberSettingField({ field, value, onChange, onBlur, t }) {
  return (
    <div className="form-group">
      <label htmlFor={field.id}>{t[field.labelKey]}</label>
      <input
        type="number"
        id={field.id}
        min={String(SETTINGS_NUMBER_MIN)}
        max={String(SETTINGS_NUMBER_MAX)}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => onBlur(e.target.value)}
      />
    </div>
  );
}

function ToggleSettingField({ field, checked, onChange, t }) {
  return (
    <div className="form-group toggle-row">
      <label htmlFor={field.id}>{t[field.labelKey]}</label>
      <label className="switch">
        <input
          type="checkbox"
          id={field.id}
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="slider round"></span>
      </label>
    </div>
  );
}

function getMatchingPresetId({ pomodoro, shortBreak, longBreak, intervals }) {
  const matchingPreset = TIMER_PRESETS.find((preset) => (
    preset.pomodoro === Number(pomodoro)
    && preset.shortBreak === Number(shortBreak)
    && preset.longBreak === Number(longBreak)
    && preset.intervals === Number(intervals)
  ));

  return matchingPreset ? matchingPreset.id : 'custom';
}

export function getPresetMeta(presetId) {
  const preset = TIMER_PRESETS.find((item) => item.id === presetId);

  if (preset) {
    return {
      id: preset.id,
      name: preset.name,
      description: preset.description,
    };
  }

  return {
    id: 'custom',
    name: 'Custom',
    description: 'Manual durations',
  };
}

function SettingsPanel({
  pomodoroTime,
  shortBreakTime,
  longBreakTime,
  numIntervals,
  enableCountdown,
  autoStartPomodoros,
  manualSkipControlsEnabled,
  manualExtensionControlsEnabled,
  showShortcutHints,
  activeThemeId,
  signalVolume,
  onApply,
  onClose,
  onTestSound = null,
}) {
  const [localPomodoro, setLocalPomodoro] = useState(pomodoroTime);
  const [localShortBreak, setLocalShortBreak] = useState(shortBreakTime);
  const [localLongBreak, setLocalLongBreak] = useState(longBreakTime);
  const [localIntervals, setLocalIntervals] = useState(numIntervals);
  const [localCountdown, setLocalCountdown] = useState(enableCountdown);
  const [localAutoStart, setLocalAutoStart] = useState(autoStartPomodoros);
  const [localManualSkipControlsEnabled, setLocalManualSkipControlsEnabled] = useState(manualSkipControlsEnabled);
  const [localManualExtensionControlsEnabled, setLocalManualExtensionControlsEnabled] = useState(manualExtensionControlsEnabled);

  const [localShowShortcutHints, setLocalShowShortcutHints] = useState(showShortcutHints);
  const [localThemeId, setLocalThemeId] = useState(activeThemeId);
  const [localSignalVolume, setLocalSignalVolume] = useState(signalVolume);

  const { lang } = useLang();
  const t = getT(lang);
  const [activeCategory, setActiveCategory] = useState('timer');

  const settingsCategories = useMemo(() => ([
    { key: 'timer', label: t.settingsSectionTimer, description: t.settingsCategoryTimerTabCopy },
    { key: 'behavior', label: t.settingsSectionBehavior, description: t.settingsCategoryBehaviorTabCopy },
    { key: 'appearance', label: t.settingsSectionAppearance, description: t.settingsCategoryAppearanceTabCopy },
    { key: 'alerts', label: t.settingsSectionAlerts, description: t.settingsCategoryAlertsTabCopy },
  ]), [t]);

  const activePresetId = useMemo(() => getMatchingPresetId({
    pomodoro: localPomodoro,
    shortBreak: localShortBreak,
    longBreak: localLongBreak,
    intervals: localIntervals,
  }), [localPomodoro, localShortBreak, localLongBreak, localIntervals]);

  const handleApply = () => {
    const validPomodoro = normalizeIntegerSetting(localPomodoro);
    const validShortBreak = normalizeIntegerSetting(localShortBreak);
    const validLongBreak = normalizeIntegerSetting(localLongBreak);
    const validIntervals = normalizeIntegerSetting(localIntervals);
    const presetId = getMatchingPresetId({
      pomodoro: validPomodoro,
      shortBreak: validShortBreak,
      longBreak: validLongBreak,
      intervals: validIntervals,
    });

    onApply(
      validPomodoro,
      validShortBreak,
      validLongBreak,
      validIntervals,
      localCountdown,
      localAutoStart,
      localManualSkipControlsEnabled,
      localManualExtensionControlsEnabled,
      presetId,
      localThemeId,
      normalizeSignalVolume(localSignalVolume),
      localShowShortcutHints
    );
  };

  const handleBlur = (setter, value) => {
    setter(normalizeIntegerSetting(value));
  };

  const handleResetDefaults = () => {
    setLocalPomodoro(SETTINGS_DEFAULTS.pomodoro);
    setLocalShortBreak(SETTINGS_DEFAULTS.shortBreak);
    setLocalLongBreak(SETTINGS_DEFAULTS.longBreak);
    setLocalIntervals(SETTINGS_DEFAULTS.intervals);
    setLocalCountdown(SETTINGS_DEFAULTS.countdown);
    setLocalAutoStart(SETTINGS_DEFAULTS.autoStart);
    setLocalManualSkipControlsEnabled(SETTINGS_DEFAULTS.skipControls);
    setLocalManualExtensionControlsEnabled(SETTINGS_DEFAULTS.extensionControls);
    setLocalShowShortcutHints(SETTINGS_DEFAULTS.showShortcutHints);
    setLocalThemeId(SETTINGS_DEFAULTS.themeId);
    setLocalSignalVolume(SETTINGS_DEFAULTS.signalVolume);
  };

  const handlePresetSelect = (preset) => {
    setLocalPomodoro(preset.pomodoro);
    setLocalShortBreak(preset.shortBreak);
    setLocalLongBreak(preset.longBreak);
    setLocalIntervals(preset.intervals);
  };

  return (
    <div id="settings-panel">
      <form id="settings-form">
        <div className="settings-category-nav" role="tablist" aria-label={t.settingsCategoryNavLabel}>
          {SETTINGS_CATEGORY_ORDER.map((categoryKey) => {
            const category = settingsCategories.find((item) => item.key === categoryKey);
            const isActive = activeCategory === categoryKey;

            return (
              <button
                key={category.key}
                type="button"
                role="tab"
                id={`settings-tab-${category.key}`}
                className={`settings-category-tab${isActive ? ' is-active' : ''}`}
                aria-selected={isActive}
                aria-controls={`settings-panel-${category.key}`}
                tabIndex={isActive ? 0 : -1}
                onClick={() => setActiveCategory(category.key)}
              >
                <span className="settings-category-tab-label">{category.label}</span>
                <span className="settings-category-tab-copy">{category.description}</span>
              </button>
            );
          })}
        </div>

        <div className="settings-body">
          {activeCategory === 'timer' && (
            <section
              className="settings-section settings-section-card settings-section-view"
              id="settings-panel-timer"
              role="tabpanel"
              aria-labelledby="settings-tab-timer"
            >
              <div className="settings-section-header">
                <div>
                  <h3>{t.settingsSectionTimer}</h3>
                  <p className="settings-section-copy">{t.settingsSectionTimerCopy}</p>
                </div>
                <p className="preset-helper" aria-live="polite">
                  {t.settingsActivePreset} <strong>{getPresetMeta(activePresetId).name}</strong>
                </p>
              </div>

              <div className="settings-subsection">
                <h4>{t.settingsSectionPresets}</h4>
                <div className="preset-grid" role="group" aria-label="Timer presets">
                  {TIMER_PRESETS.map((preset) => {
                    const isActive = preset.id === activePresetId;

                    return (
                      <button
                        key={preset.id}
                        type="button"
                        className={`preset-chip${isActive ? ' is-active' : ''}`}
                        aria-pressed={isActive}
                        onClick={() => handlePresetSelect(preset)}
                      >
                        <span className="preset-chip-name">{preset.name}</span>
                        <span className="preset-chip-copy">{t.presetDescriptions?.[preset.id] || preset.description}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="settings-subsection">
                <h4>{t.settingsSectionTime}</h4>
                <div className="time-grid">
                  {TIMER_FIELD_CONFIG.map((field) => {
                    const fieldState = {
                      pomodoro: [localPomodoro, setLocalPomodoro],
                      shortBreak: [localShortBreak, setLocalShortBreak],
                      longBreak: [localLongBreak, setLocalLongBreak],
                      intervals: [localIntervals, setLocalIntervals],
                    }[field.key];
                    const [value, setter] = fieldState;

                    return (
                      <NumberSettingField
                        key={field.id}
                        field={field}
                        value={value}
                        onChange={setter}
                        onBlur={(nextValue) => handleBlur(setter, nextValue)}
                        t={t}
                      />
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          {activeCategory === 'behavior' && (
            <section
              className="settings-section settings-section-card settings-section-view"
              id="settings-panel-behavior"
              role="tabpanel"
              aria-labelledby="settings-tab-behavior"
            >
              <div className="settings-section-header">
                <div>
                  <h3>{t.settingsSectionBehavior}</h3>
                  <p className="settings-section-copy">{t.settingsSectionBehaviorCopy}</p>
                </div>
              </div>
              <div className="settings-toggle-list">
                {BEHAVIOR_TOGGLE_CONFIG.map((field) => {
                  const fieldState = {
                    countdown: [localCountdown, setLocalCountdown],
                    autoStart: [localAutoStart, setLocalAutoStart],
                    skipControls: [localManualSkipControlsEnabled, setLocalManualSkipControlsEnabled],
                    extensionControls: [localManualExtensionControlsEnabled, setLocalManualExtensionControlsEnabled],
                  }[field.key];
                  const [checked, setter] = fieldState;

                  return (
                    <ToggleSettingField
                      key={field.id}
                      field={field}
                      checked={checked}
                      onChange={setter}
                      t={t}
                    />
                  );
                })}
              </div>
            </section>
          )}

          {activeCategory === 'appearance' && (
            <section
              className="settings-section settings-section-card settings-section-view"
              id="settings-panel-appearance"
              role="tabpanel"
              aria-labelledby="settings-tab-appearance"
            >
              <div className="settings-section-header">
                <div>
                  <h3>{t.settingsSectionAppearance}</h3>
                  <p className="settings-section-copy">{t.settingsSectionAppearanceCopy}</p>
                </div>
                <p className="preset-helper" aria-live="polite">
                  {t.settingsActiveTheme} <strong>{getThemeMeta(localThemeId).name}</strong>
                </p>
              </div>

              <div className="settings-subsection">
                <h4>{t.settingsSectionTheme}</h4>
                <div className="theme-grid" role="radiogroup" aria-label="Color themes">
                  {APP_THEMES.map((theme) => {
                    const isActive = theme.id === localThemeId;

                    return (
                      <button
                        key={theme.id}
                        type="button"
                        className={`theme-chip theme-chip-${theme.id}${isActive ? ' is-active' : ''}`}
                        role="radio"
                        aria-checked={isActive}
                        onClick={() => setLocalThemeId(theme.id)}
                      >
                        <span className="theme-chip-swatch" aria-hidden="true">
                          {theme.swatches.map((swatch) => (
                            <span key={swatch} style={{ background: swatch }}></span>
                          ))}
                        </span>
                        <span className="theme-chip-name">{theme.name}</span>
                        <span className="theme-chip-copy">{t.themeDescriptions?.[theme.id] || theme.description}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="settings-toggle-list">
                {APPEARANCE_TOGGLE_CONFIG.map((field) => (
                  <ToggleSettingField
                    key={field.id}
                    field={field}
                    checked={localShowShortcutHints}
                    onChange={setLocalShowShortcutHints}
                    t={t}
                  />
                ))}
              </div>
            </section>
          )}

          {activeCategory === 'alerts' && (
            <section
              className="settings-section settings-section-card settings-section-view"
              id="settings-panel-alerts"
              role="tabpanel"
              aria-labelledby="settings-tab-alerts"
            >
              <div className="settings-section-header">
                <div>
                  <h3>{t.settingsSectionAlerts}</h3>
                  <p className="settings-section-copy">{t.settingsSectionAlertsCopy}</p>
                </div>
              </div>

              <div className="settings-subsection">
                <h4>{t.settingsSectionSignal}</h4>
                <div className="form-group">
                  <label htmlFor="signal-volume">{t.settingsLabelSignalVolume} ({Math.round(localSignalVolume * 100)}%)</label>
                  <input
                    type="range"
                    id="signal-volume"
                    min="0"
                    max="1"
                    step="0.05"
                    value={localSignalVolume}
                    onChange={(e) => setLocalSignalVolume(Number.parseFloat(e.target.value))}
                  />
                </div>
                <button
                  type="button"
                  className="secondary-action-btn"
                  onClick={() => onTestSound?.(normalizeSignalVolume(localSignalVolume))}
                >
                  {t.settingsBtnTestSound}
                </button>
              </div>

            </section>
          )}
        </div>

        <div className="settings-buttons">
          <button type="button" id="apply-settings" onClick={handleApply}>
            {t.settingsBtnApply}
          </button>
          <button type="button" id="reset-defaults" onClick={handleResetDefaults}>
            {t.settingsBtnResetDefaults}
          </button>
          <button type="button" id="close-settings" onClick={onClose}>
            {t.settingsBtnClose}
          </button>
        </div>
      </form>
    </div>
  );
}

export default SettingsPanel;
